import { Selector, State } from '@ngxs/store';
import { itemsPerPage } from '@shared/helpers';
import { IPlaylist, IPlaylistHot } from '@interfaces/playlist';
import { Observable } from 'rxjs';
import { IResponse } from '@interfaces/response';
import { tap } from 'rxjs/operators';
import { PlaylistsService } from '@services/playlists/playlists.service';
import { SidebarHotPlaylistsStateModel } from '@store/sidebar-state/hot-playlists/sidebar.hot-playlists-state.model';
import { Injectable } from '@angular/core';
import { NgxsDataRepository } from '@angular-ru/ngxs/repositories';
import { Payload, StateRepository } from '@angular-ru/ngxs/decorators';
import { DataActionWithoutSubscription } from '@shared/functions';

@StateRepository()
@State<SidebarHotPlaylistsStateModel>({
    name: 'SidebarHotPlaylistTab',
    defaults: {
        currentPage: 1,
        hotPlaylistPerPage: itemsPerPage[5],
        totalPlaylists: 0,
        loader: false,
        playlists: [],
        activeHotPlaylist: null,
        searchParam: '',
    }
})

@Injectable()
export class SidebarHotPlaylistsState extends NgxsDataRepository<SidebarHotPlaylistsStateModel> {
    constructor(private playlistService: PlaylistsService) {
        super();
    }

    @Selector([SidebarHotPlaylistsState])
    static activeHotPlaylist(state: SidebarHotPlaylistsStateModel): IPlaylist | null {
        return state.activeHotPlaylist;
    }

    @DataActionWithoutSubscription({cancelUncompleted: true})
    getHotSidebarPlaylists(): Observable<IResponse<IPlaylist>> {
        const state = this.ctx.getState();

        if (!state) {
            return this.playlistService.fetchPlaylists({
                limit: itemsPerPage[5],
                skip: 0,
                search: '',
                type: 'hot'
            });
        }

        const limit = state.hotPlaylistPerPage;
        const skip = limit * (state.currentPage - 1);
        const search = state.searchParam;

        this.ctx.patchState({loader: true});
        return this.playlistService.fetchPlaylists({
            limit,
            skip,
            search,
            type: 'hot'
            }).pipe(
                tap(data => {
                    this.ctx.patchState({ playlists: data.rows, totalPlaylists: data.total, loader: false});
                })
            );
    }

    @DataActionWithoutSubscription()
    getPlaylistsByPage(
        @Payload('[Sidebar media hot playlists] get playlists by page') page: number
    ): Observable<any> {
        this.ctx.patchState({currentPage: page});
        return this.getHotSidebarPlaylists();
    }

    @DataActionWithoutSubscription()
    setActivePlaylist(
        @Payload('[Sidebar media hot playlists] set active hot playlist') playlist: IPlaylistHot
    ): void {
        this.ctx.patchState({activeHotPlaylist: playlist});
    }

    @DataActionWithoutSubscription()
    setSidebarHotPlaylistPerPage(
        @Payload('[Sidebar media hot playlists] set playlist per page') hotPlaylistPerPage: number
    ): Observable<any> {
        this.ctx.patchState({hotPlaylistPerPage, currentPage: 1});
        return this.getHotSidebarPlaylists();
    }

    @DataActionWithoutSubscription()
    setSidebarSearchParam(
        @Payload('[Sidebar media hot playlists] set search param') searchParam: string
    ): Observable<any> {
        this.ctx.patchState({currentPage: 1, searchParam});
        return this.getHotSidebarPlaylists();
    }

    @DataActionWithoutSubscription()
    refreshAfterDelete(
        @Payload('[Sidebar media hot playlists] refresh after delete') deletedCount = 1
    ): Observable<any> {
        const state = this.ctx.getState();
        const totalAfterDelete = Math.max(state.totalPlaylists - deletedCount, 0);
        const lastAvailablePage = Math.max(Math.ceil(totalAfterDelete / state.hotPlaylistPerPage), 1);
        const currentPage = Math.min(state.currentPage, lastAvailablePage);

        this.ctx.patchState({currentPage});
        return this.getHotSidebarPlaylists();
    }
}
